"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import LanguageSwitcher from "@/components/shared/LanguageSwitcher";
import BrandLogo from "@/components/BrandLogo";

/* ------------------------------------------------------------------ */
/*  Footer — shared across all public pages                           */
/*  variant="full"   → 4-column grid (landing page)                   */
/*  variant="simple" → single row with links (default, all other pages) */
/* ------------------------------------------------------------------ */

function ManageCookiesButton() {
  return (
    <button
      type="button"
      onClick={() => window.dispatchEvent(new Event("manage-cookies"))}
      className="hover:text-gray-700 dark:hover:text-white underline cursor-pointer"
    >
      Manage Cookies
    </button>
  );
}

/* ---------- SOCIAL ICONS ---------- */

function SocialIcons({ className = "" }: { className?: string }) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <a href="https://twitter.com/AdityaLabs" target="_blank" rel="noopener noreferrer" aria-label="Follow us on X (Twitter)" className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white transition">
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
      </a>
      <a href="https://linkedin.com/company/adityalabs" target="_blank" rel="noopener noreferrer" aria-label="Follow us on LinkedIn" className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white transition">
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
      </a>
      <a href="https://github.com/adityalabs" target="_blank" rel="noopener noreferrer" aria-label="View our GitHub" className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white transition">
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" /></svg>
      </a>
    </div>
  );
}

/* ---------- SIMPLE FOOTER (most pages) ---------- */

function SimpleFooter() {
  const t = useTranslations("footer");
  return (
    <footer className="dark border-t border-gray-200 dark:border-white/10 py-8 px-6 bg-navy dark:bg-navy">
      <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <p className="text-xs text-gray-600 dark:text-gray-300">
            &copy; {new Date().getFullYear()} Aditya Labs. {t("copyright")}
          </p>
          <SocialIcons />
        </div>
        <div className="flex flex-wrap gap-4 text-xs text-gray-600 dark:text-gray-300">
          <Link href="/about" className="hover:text-gray-700 dark:hover:text-white">{t("about")}</Link>
          <Link href="/pricing" className="hover:text-gray-700 dark:hover:text-white">{t("pricing")}</Link>
          <Link href="/contact" className="hover:text-gray-700 dark:hover:text-white">{t("contact")}</Link>
          <Link href="/privacy" className="hover:text-gray-700 dark:hover:text-white">{t("privacy")}</Link>
          <Link href="/terms" className="hover:text-gray-700 dark:hover:text-white">{t("terms")}</Link>
          <Link href="/gdpr" className="hover:text-gray-700 dark:hover:text-white">GDPR</Link>
          <Link href="/ai-compliance" className="hover:text-gray-700 dark:hover:text-white">AI Compliance</Link>
          <Link href="/cookies" className="hover:text-gray-700 dark:hover:text-white">Cookie Policy</Link>
          <Link href="/security" className="hover:text-gray-700 dark:hover:text-white">{t("security")}</Link>
          <ManageCookiesButton />
          <LanguageSwitcher />
        </div>
      </div>
    </footer>
  );
}

/* ---------- FULL FOOTER (landing page) ---------- */

function FullFooter() {
  const t = useTranslations("footer");
  return (
    <footer className="dark border-t border-gray-200 dark:border-white/10 py-12 px-6 bg-navy dark:bg-navy">
      <div className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <BrandLogo size={40} showName asset="footer" className="mb-4" linked={false} />
            <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed mb-3">
              AI agents for small businesses. Automate customer service, lead qualification, and appointments.
            </p>
            <SocialIcons />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Product</p>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
              <li><Link href="/#features" className="hover:text-gray-700 dark:hover:text-white">Features</Link></li>
              <li><Link href="/pricing" className="hover:text-gray-700 dark:hover:text-white">Pricing</Link></li>
              <li><Link href="/enterprise" className="hover:text-gray-700 dark:hover:text-white">Enterprise</Link></li>
              <li><Link href="/template-library" className="hover:text-gray-700 dark:hover:text-white">Templates</Link></li>
              <li><Link href="/case-studies" className="hover:text-gray-700 dark:hover:text-white">Industry Research</Link></li>
              <li><Link href="/compare" className="hover:text-gray-700 dark:hover:text-white">Compare</Link></li>
              <li><Link href="/roi-calculator" className="hover:text-gray-700 dark:hover:text-white">ROI Calculator</Link></li>
              <li><Link href="/changelog" className="hover:text-gray-700 dark:hover:text-white">Changelog</Link></li>
              <li><Link href="/docs" className="hover:text-gray-700 dark:hover:text-white">API Docs</Link></li>
              <li><Link href="/status" className="hover:text-gray-700 dark:hover:text-white">Status</Link></li>
              <li><Link href="/login" className="hover:text-gray-700 dark:hover:text-white">Dashboard</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Company</p>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
              <li><Link href="/about" className="hover:text-gray-700 dark:hover:text-white">About</Link></li>
              <li><Link href="/transparency" className="hover:text-gray-700 dark:hover:text-white">Transparency</Link></li>
              <li><Link href="/contact" className="hover:text-gray-700 dark:hover:text-white">Contact Us</Link></li>
              <li><Link href="/blog" className="hover:text-gray-700 dark:hover:text-white">Blog</Link></li>
              <li><Link href="/terms" className="hover:text-gray-700 dark:hover:text-white">{t("terms")}</Link></li>
              <li><Link href="/privacy" className="hover:text-gray-700 dark:hover:text-white">{t("privacy")}</Link></li>
              <li><Link href="/gdpr" className="hover:text-gray-700 dark:hover:text-white">GDPR</Link></li>
              <li><Link href="/cookies" className="hover:text-gray-700 dark:hover:text-white">Cookie Policy</Link></li>
              <li><Link href="/ai-compliance" className="hover:text-gray-700 dark:hover:text-white">AI Compliance</Link></li>
              <li><Link href="/security" className="hover:text-gray-700 dark:hover:text-white">Security & Trust</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Solutions</p>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
              <li><Link href="/real-estate" className="hover:text-gray-700 dark:hover:text-white">Real Estate</Link></li>
              <li><Link href="/dental" className="hover:text-gray-700 dark:hover:text-white">Dental</Link></li>
              <li><Link href="/law-firms" className="hover:text-gray-700 dark:hover:text-white">Law Firms</Link></li>
              <li><Link href="/fitness" className="hover:text-gray-700 dark:hover:text-white">Fitness</Link></li>
              <li><Link href="/restaurants" className="hover:text-gray-700 dark:hover:text-white">Restaurants</Link></li>
              <li><Link href="/recruitment" className="hover:text-gray-700 dark:hover:text-white">Recruitment</Link></li>
              <li><Link href="/industries" className="hover:text-gray-700 dark:hover:text-white font-medium text-blue-600 dark:text-blue-400">All Industries →</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-200 dark:border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-gray-600 dark:text-gray-300">
            &copy; {new Date().getFullYear()} Aditya Labs. {t("copyright")}
          </p>
          <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 dark:text-gray-300">
            <span>Payments by Stripe</span>
            <span>&middot;</span>
            <span>Hosted on Vercel</span>
            <span>&middot;</span>
            <ManageCookiesButton />
            <span>&middot;</span>
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ---------- EXPORT ---------- */

export default function Footer({ variant = "simple" }: { variant?: "simple" | "full" }) {
  return variant === "full" ? <FullFooter /> : <SimpleFooter />;
}
